import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Users, Store, Package, EyeOff, ShoppingBag, DollarSign, Eye, Trash2, Loader2, ExternalLink, Clock, Check, UserX, UserCheck, Plus, Pencil } from "lucide-react";

const Metric = ({ icon: Icon, label, value }) => (
  <div className="bg-white border border-[#E2E8F0] p-5">
    <div className="flex items-center gap-2 text-[#94A3B8] text-[11px] font-bold uppercase tracking-widest mb-2">
      <Icon className="h-4 w-4" /> {label}
    </div>
    <div className="font-display text-2xl font-extrabold text-[#0F172A]">{value}</div>
  </div>
);

const STATUS_STYLE = {
  published: "bg-emerald-50 text-emerald-600",
  hidden: "bg-red-50 text-red-600",
  pending: "bg-amber-50 text-amber-600",
};

export default function Admin() {
  const { t } = useI18n();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (ready && (user === false || (user && user.role !== "admin"))) navigate("/");
  }, [ready, user, navigate]);

  const refreshStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status !== "all") params.set("status", status);
      const { data } = await api.get(`/admin/products?${params.toString()}`);
      setProducts(data);
    } catch { setProducts([]); }
    finally { setLoading(false); }
  }, [q, status]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { if (isAdmin) refreshStats(); }, [isAdmin, refreshStats]);
  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "products") loadProducts(); else loadUsers();
  }, [isAdmin, tab, loadProducts, loadUsers]);

  const setProductStatus = async (p, next) => {
    try {
      const { data } = await api.patch(`/admin/products/${p.id}/status`, { status: next });
      setProducts((list) => list.map((x) => (x.id === p.id ? data : x)));
      refreshStats();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const delProduct = async (p) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      setProducts((list) => list.filter((x) => x.id !== p.id));
      toast.success(t("admin.delete"));
      refreshStats();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const toggleSuspend = async (u) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}/suspend`, { suspended: !u.suspended });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!ready || user === null) return <div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0EA5E9]" /></div>;
  if (!isAdmin) return null;

  const TabBtn = ({ id, label, icon: Icon }) => (
    <button onClick={() => { setTab(id); setQ(""); }} data-testid={`admin-tab-${id}`}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition-colors ${tab === id ? "bg-[#0F172A] text-white" : "bg-white border border-[#E2E8F0] text-[#475569] hover:border-[#0EA5E9]"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="flex items-center gap-2 mb-8">
        <Shield className="h-6 w-6 text-[#0EA5E9]" />
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0F172A]">{t("admin.title")}</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-7 border border-[#E2E8F0] rounded-2xl overflow-hidden divide-x divide-y lg:divide-y-0 divide-[#E2E8F0] mb-8" data-testid="admin-stats">
          <Metric icon={Users} label={t("admin.users")} value={stats.users} />
          <Metric icon={Store} label={t("admin.sellers")} value={stats.sellers} />
          <Metric icon={Package} label={t("admin.products")} value={stats.products} />
          <Metric icon={Clock} label={t("admin.pending")} value={stats.pending ?? 0} />
          <Metric icon={EyeOff} label={t("admin.hidden")} value={stats.hidden} />
          <Metric icon={ShoppingBag} label={t("admin.orders")} value={stats.orders} />
          <Metric icon={DollarSign} label={t("admin.revenue")} value={`$${stats.revenue.toFixed(2)}`} />
        </div>
      )}

      <div className="flex gap-3 mb-6">
        <TabBtn id="products" label={t("admin.tabProducts")} icon={Package} />
        <TabBtn id="users" label={t("admin.tabUsers")} icon={Users} />
        {tab === "products" && (
          <Button onClick={() => navigate("/dashboard/new")} data-testid="admin-new-product"
            className="ml-auto rounded-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold">
            <Plus className="h-4 w-4 mr-1" /> {t("dash.newProduct")}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.search")}
          data-testid="admin-search" className="rounded-full bg-white flex-1 min-w-[220px]" />
        {tab === "products" && (
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[180px] rounded-full bg-white" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin.all")}</SelectItem>
              <SelectItem value="pending">{t("admin.statusPending")}</SelectItem>
              <SelectItem value="published">{t("admin.published")}</SelectItem>
              <SelectItem value="hidden">{t("admin.statusHidden")}</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[#0EA5E9]" /></div>
      ) : tab === "products" ? (
        products.length === 0 ? (
          <div className="text-center py-24 text-[#94A3B8]" data-testid="admin-empty">{t("admin.empty")}</div>
        ) : (
          <div className="border border-[#E2E8F0] rounded-2xl bg-white divide-y divide-[#E2E8F0]" data-testid="admin-products">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`admin-product-${p.id}`}>
                <img src={fileUrl(p.preview_image_url)} alt="" className="h-14 w-14 rounded-xl object-cover bg-slate-100 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#0F172A] truncate">{p.title}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_STYLE[p.status] || ""}`}>{p.status}</span>
                  </div>
                  <div className="text-sm text-[#94A3B8] truncate">{p.category} · ${Number(p.price).toFixed(2)} · {p.seller_name}</div>
                </div>
                <Link to={`/product/${p.id}`} target="_blank" className="text-[#94A3B8] hover:text-[#0EA5E9]" data-testid={`admin-view-${p.id}`}><ExternalLink className="h-4 w-4" /></Link>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => navigate(`/dashboard/edit/${p.id}`)} data-testid={`admin-edit-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {p.status === "pending" && (
                  <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setProductStatus(p, "published")} data-testid={`admin-approve-${p.id}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> {t("admin.approve")}
                  </Button>
                )}
                {p.status === "published" && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setProductStatus(p, "hidden")} data-testid={`admin-toggle-${p.id}`}>
                    <EyeOff className="h-3.5 w-3.5 mr-1" /> {t("admin.hide")}
                  </Button>
                )}
                {p.status === "hidden" && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => setProductStatus(p, "published")} data-testid={`admin-toggle-${p.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> {t("admin.publish")}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="rounded-full text-red-600 hover:bg-red-50" onClick={() => delProduct(p)} data-testid={`admin-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )
      ) : (
        users.length === 0 ? (
          <div className="text-center py-24 text-[#94A3B8]" data-testid="admin-users-empty">{t("admin.usersEmpty")}</div>
        ) : (
          <div className="border border-[#E2E8F0] rounded-2xl bg-white divide-y divide-[#E2E8F0]" data-testid="admin-users">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-4 p-4" data-testid={`admin-user-${u.id}`}>
                <div className="h-11 w-11 rounded-full bg-[#0F172A] text-white font-bold flex items-center justify-center shrink-0">{(u.name || "U")[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#0F172A] truncate">{u.name}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 bg-slate-100 text-[#475569]">{u.role}</span>
                    {u.suspended && <span className="text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 bg-red-50 text-red-600">{t("admin.suspended")}</span>}
                  </div>
                  <div className="text-sm text-[#94A3B8] truncate">{u.email}</div>
                </div>
                {u.role !== "admin" && (
                  <Button size="sm" variant="outline"
                    className={`rounded-full ${u.suspended ? "text-emerald-600 hover:bg-emerald-50" : "text-red-600 hover:bg-red-50"}`}
                    onClick={() => toggleSuspend(u)} data-testid={`admin-suspend-${u.id}`}>
                    {u.suspended ? <><UserCheck className="h-3.5 w-3.5 mr-1" /> {t("admin.unsuspend")}</> : <><UserX className="h-3.5 w-3.5 mr-1" /> {t("admin.suspend")}</>}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
